import { API } from "aws-amplify";
import { useEffect, useState } from "react";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import { compareArrays } from "utils/helpers";
import { toast } from "react-toastify";
import { toastifyConfig } from "../components/ReferralForm/ReferralForm.common";
import { Stack } from "@mui/material";
import { ReactComponent as SuccessIcon } from "images/success.svg";

export const useUserLocations = ({ user }) => {
  const dispatch = useAppDispatch();

  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  const [locations, setLocations] = useState({});
  const [isSelectedLocationLoaded, setIsSelectedLocationLoaded] =
    useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [prevSelectedLocations, setPrevSelectedLocations] = useState([]);

  const resetSelectedLocations = () =>
    setSelectedLocations([...prevSelectedLocations]);

  const isSelectedLocationsChanged = !compareArrays(
    selectedLocations,
    prevSelectedLocations
  );

  const loadLocations = async () => {
    setIsLocationLoaded(false);
    try {
      const result = await API.post("restApi", "/locations", {
        body: {
          action: "locationOptions",
          organizationId: user?.organizationId,
        },
      });
      const locations = Object.entries(result);
      locations.sort((a, b) => a[1].localeCompare(b[1]));
      setLocations(locations);
    } catch (e) {
      const message = e?.response?.data?.message ?? e?.response?.data?.error;
      dispatch(showError(message ?? "Error loading locations"));
      setLocations([]);
    } finally {
      setIsLocationLoaded(true);
    }
  };

  const handleChangeLocation = (e, id) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedLocations([...selectedLocations, id]);
    } else {
      setSelectedLocations(
        selectedLocations.filter((locationId) => locationId !== id)
      );
    }
  };

  const updateSelectedLocations = async () => {
    dispatch(showSpinner());
    let result = true;
    try {
      await API.post("restApiV2", `/locations/users`, {
        body: {
          marketerId: user?.id,
          userId: user?.id,
          locationIds: selectedLocations,
        },
      });
      setPrevSelectedLocations(selectedLocations);
      toast(
        () => (
          <Stack direction={"row"} gap={1}>
            <SuccessIcon />
            <Stack>
              <div className="text-sm">{user.role} updated successfully.</div>
            </Stack>
          </Stack>
        ),
        // @ts-ignore
        toastifyConfig
      );
    } catch (e) {
      const message = e?.response?.data?.message ?? e?.response?.data?.error;
      dispatch(showError(message ?? "Error updating selected locations"));
      result = false;
    } finally {
      setIsSelectedLocationLoaded(true);
      dispatch(hideSpinner());
    }
    return result;
  };

  useEffect(() => {
    const fetchLocations = async () => {
      setIsSelectedLocationLoaded(false);
      try {
        // @ts-ignore
        const result = await API.get(
          "restApiV2",
          `/locations/users/${user?.id}`,
          {}
        );
        const mappedLocations = result.map((location) => String(location.id));
        setSelectedLocations(mappedLocations);
        setPrevSelectedLocations(mappedLocations);
      } catch (e) {
        const message = e?.response?.data?.message ?? e?.response?.data?.error;
        dispatch(showError(message ?? "Error loading selected locations"));
        setSelectedLocations([]);
        setPrevSelectedLocations([]);
      } finally {
        setIsSelectedLocationLoaded(true);
      }
    };

    if (user?.id && ["Marketer", "Scheduler"].includes(user?.role)) {
      loadLocations();
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    setSelectedLocations([]);
    setLocations([]);
    setIsLocationLoaded(false);
    setIsSelectedLocationLoaded(false);
  }, []);

  return {
    isLocationDataLoaded: isLocationLoaded && isSelectedLocationLoaded,
    locations,
    selectedLocations,
    setSelectedLocations,
    handleChangeLocation,
    isSelectedLocationsChanged,
    resetSelectedLocations,
    updateSelectedLocations,
  };
};
